import { useCallback, useMemo, useState } from 'react';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import {
  CaseStudiesSpotProps,
  CaseStudyTileProps,
  GridConfigs,
  PatternConfig,
} from 'components/CaseStudies/caseStudiesTypes';
import CaseStudyTile from 'components/CaseStudies/CaseStudyTile';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

import CaseStudiesFilter from './CaseStudiesFilter';

const CaseStudiesSpot = ({ fields }: CaseStudiesSpotProps) => {
  const { caseStudies, industryLabel, productLabel, softwareLabel, buttonLabel, trackingSpotName } =
    fields;
  const spotRef = useSpotTracking(trackingSpotName?.value);

  const [filteredCaseStudies, setFilteredCaseStudies] = useState<CaseStudyTileProps[]>(caseStudies);

  const handleFilterChange = useCallback((filtered: CaseStudyTileProps[]) => {
    setFilteredCaseStudies(filtered);
  }, []);

  const gridClassConfigs = useMemo<GridConfigs>(
    () => ({
      xl: {
        patterns: [
          'xl:col-span-2 xl:row-span-2',
          'xl:col-span-2 xl:row-span-1',
          'xl:col-span-1 xl:row-span-1',
          'xl:col-span-1 xl:row-span-1',
          'xl:col-span-1 xl:row-span-2',
          'xl:col-span-3 xl:row-span-2',
          'xl:col-span-1 xl:row-span-1',
          'xl:col-span-1 xl:row-span-1',
          'xl:col-span-1 xl:row-span-1',
          'xl:col-span-1 xl:row-span-1',
        ],
        fallback: 'xl:col-span-1 xl:row-span-1',
        modulus: 10,
      },
      lg: {
        patterns: [
          'lg:col-span-2 lg:row-span-2',
          'lg:col-span-1 lg:row-span-1',
          'lg:col-span-1 lg:row-span-1',
          'lg:col-span-1 lg:row-span-1',
          'lg:col-span-2 lg:row-span-2',
          'lg:col-span-1 lg:row-span-1',
          'lg:col-span-2 lg:row-span-1',
          'lg:col-span-1 lg:row-span-1',
          'lg:col-span-1 lg:row-span-1',
          'lg:col-span-2 lg:row-span-1',
        ],
        fallback: 'lg:col-span-1 lg:row-span-1',
        modulus: 10,
      },
      md: {
        patterns: [
          'md:col-span-2 md:row-span-2',
          'md:col-span-1 md:row-span-1',
          'md:col-span-1 md:row-span-2',
          'md:col-span-1 md:row-span-1',
          'md:col-span-2 md:row-span-1',
        ],
        fallback: 'md:col-span-1 md:row-span-1',
        modulus: 5,
      },
      sm: {
        pattern: 'col-span-1 row-span-1',
      },
    }),
    []
  );

  const getBreakpointClasses = (
    breakpoint: 'xl' | 'lg' | 'md' | 'sm',
    index: number,
    hasVideo?: boolean
  ) => {
    if (breakpoint === 'sm') {
      return hasVideo ? 'col-span-1 row-span-2' : gridClassConfigs.sm.pattern;
    }

    const config = gridClassConfigs[breakpoint] as PatternConfig;
    const pattern = index % config.modulus;
    return config.patterns[pattern] || config.fallback;
  };

  const getGridClasses = (index: number, caseStudy: CaseStudyTileProps) => {
    const hasVideo = Boolean(caseStudy.youtubeVideo?.value || caseStudy.youkuVideo?.value);

    return `${getBreakpointClasses('sm', index, hasVideo)} ${getBreakpointClasses('md', index)} ${getBreakpointClasses('lg', index)} ${getBreakpointClasses('xl', index)}`;
  };

  return (
    <div ref={spotRef} data-wt-spot={trackingSpotName?.value} className="container">
      <CaseStudiesFilter
        caseStudies={caseStudies}
        productLabel={productLabel.value}
        industryLabel={industryLabel.value}
        softwareLabel={softwareLabel.value}
        buttonLabel={buttonLabel.value}
        onFilterChange={handleFilterChange}
      />
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        style={{ gridAutoRows: '318px' }}
      >
        {filteredCaseStudies?.map((caseStudy, index) => (
          <div key={caseStudy.id} className={getGridClasses(index, caseStudy)}>
            <CaseStudyTile {...caseStudy} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default withDatasourceCheck()(CaseStudiesSpot);
